import clsx from 'clsx';
import { AlertTriangle, ArrowRight, CheckCircle2, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@atoms/Button';
import { Badge } from '@atoms/Badge';
import type { Order, OrderStatus } from '@/types';
import { formatCurrency, formatDate, ORDER_STATUS_LABELS, orderStatusBadgeVariant } from '@utils/formatters';

export type OrderStatusAction = 'approve' | 'complete' | 'cancel';

interface ActionConfig {
  title: string;
  description: string;
  confirmLabel: string;
  targetStatus: OrderStatus;
  tone: 'success' | 'danger' | 'neutral';
  icon: LucideIcon;
}

const ACTION_CONFIG: Record<OrderStatusAction, ActionConfig> = {
  approve: {
    title: 'Siparişi Onayla',
    description:
      'Bu siparişi onayladığınızda restoran bilgilendirilir ve hazırlık süreci başlatılır.',
    confirmLabel: 'Onayla',
    targetStatus: 'approved',
    tone: 'success',
    icon: CheckCircle2,
  },
  complete: {
    title: 'Siparişi Tamamla',
    description:
      'Sipariş teslim edildi veya teslim alındı olarak işaretlenecek. Bu işlem geri alınamaz.',
    confirmLabel: 'Tamamlandı İşaretle',
    targetStatus: 'completed',
    tone: 'success',
    icon: CheckCircle2,
  },
  cancel: {
    title: 'Siparişi İptal Et',
    description:
      'Sipariş iptal edilecek ve restoran tarafına yansıyacaktır. Onaylanmış siparişlerde stok hareketleri geri alınabilir.',
    confirmLabel: 'İptal Et',
    targetStatus: 'cancelled',
    tone: 'danger',
    icon: XCircle,
  },
};

const toneStyles = {
  success: {
    iconWrap: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
    accent: 'text-emerald-700',
  },
  danger: {
    iconWrap: 'bg-red-50 text-red-600 ring-red-100',
    accent: 'text-red-700',
  },
  neutral: {
    iconWrap: 'bg-slate-100 text-slate-600 ring-slate-200',
    accent: 'text-slate-700',
  },
};

interface OrderActionConfirmDialogProps {
  open: boolean;
  order: Order | null;
  action: OrderStatusAction | null;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function OrderActionConfirmDialog({
  open,
  order,
  action,
  loading,
  onClose,
  onConfirm,
}: OrderActionConfirmDialogProps) {
  if (!open || !order || !action) return null;

  const config = ACTION_CONFIG[action];
  const Icon = config.icon;
  const styles = toneStyles[config.tone];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Kapat"
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
        onClick={loading ? undefined : onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-action-title"
        className="relative z-10 w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200"
      >
        <div className="border-b border-slate-100 px-6 py-5">
          <div className="flex items-start gap-4">
            <div
              className={clsx(
                'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-4',
                styles.iconWrap,
              )}
            >
              <Icon className="h-6 w-6" />
            </div>
            <div className="min-w-0 pt-0.5">
              <h2 id="order-action-title" className="text-lg font-semibold text-slate-900">
                {config.title}
              </h2>
              <p className="mt-1 text-sm leading-relaxed text-slate-500">{config.description}</p>
            </div>
          </div>
        </div>

        <div className="space-y-4 px-6 py-5">
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Sipariş</p>
                <p className="mt-1 font-semibold text-slate-900">#{order.id.slice(0, 8).toUpperCase()}</p>
                <p className="mt-1 text-sm text-slate-600">
                  {order.restaurant?.name ?? 'Restoran'}
                  {order.restaurant?.username ? ` · @${order.restaurant.username}` : ''}
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-slate-900">{formatCurrency(order.totalPrice)}</p>
                <p className="mt-1 text-xs text-slate-500">{formatDate(order.createdAt)}</p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-4">
              <Badge variant={orderStatusBadgeVariant(order.status)}>
                {ORDER_STATUS_LABELS[order.status]}
              </Badge>
              <ArrowRight className="h-4 w-4 text-slate-400" />
              <Badge variant={orderStatusBadgeVariant(config.targetStatus)}>
                {ORDER_STATUS_LABELS[config.targetStatus]}
              </Badge>
            </div>

            {order.items?.length ? (
              <ul className="mt-4 space-y-1 border-t border-slate-200 pt-4 text-sm text-slate-600">
                {order.items.slice(0, 3).map((item) => (
                  <li key={item.id ?? item.productId} className="flex justify-between gap-3">
                    <span className="truncate">
                      {item.productName} × {item.quantity}
                    </span>
                    <span className="shrink-0 font-medium">{formatCurrency(item.lineTotal)}</span>
                  </li>
                ))}
                {order.items.length > 3 && (
                  <li className="text-xs text-slate-400">+{order.items.length - 3} ürün daha</li>
                )}
              </ul>
            ) : null}
          </div>

          {action === 'cancel' && (
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>Bu işlem sipariş durumunu kalıcı olarak değiştirir. Devam etmeden önce bilgileri kontrol edin.</p>
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50/60 px-6 py-4 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Vazgeç
          </Button>
          <Button
            variant={config.tone === 'danger' ? 'danger' : 'primary'}
            loading={loading}
            onClick={onConfirm}
          >
            {config.confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function getOrderStatusAction(status: OrderStatus): OrderStatusAction | null {
  if (status === 'pending') return 'approve';
  if (status === 'approved') return 'complete';
  return null;
}

export function getTargetStatusForAction(action: OrderStatusAction): OrderStatus {
  return ACTION_CONFIG[action].targetStatus;
}
